import { Box, Button, CardContent, Checkbox, FormControlLabel, Typography, Chip } from '@mui/material'
import React, { useState } from 'react'
import Grid from '@mui/material/Grid2'
import { DownloadCardComponentProps } from '../../Interface/Index';
import { getIntactnessRangeCategory } from '../../constants/index';
import { convertToNumber } from '../../constants/index';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import HearingIcon from '@mui/icons-material/Hearing';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';

interface SpeciesCardItemProps {
    index: number;
    title: React.ReactNode;
    dateTitle: string;
    date: string;
    scoreTitle: string;
    score: string;
    url: string;
    isSelected: boolean;
    onToggleSelect: () => void;
}

const SpeciesCardItem: React.FC<SpeciesCardItemProps> = ({
    index,
    title,
    dateTitle,
    date,
    scoreTitle,
    score,
    url,
    isSelected,
    onToggleSelect
}) => {
    const [imgError, setImgError] = useState(false);

    React.useEffect(() => {
        setImgError(false);
    }, [url]);

    const isAudioFile = url?.toLowerCase()?.endsWith('.wav') || url?.toLowerCase()?.endsWith('.mp4');

    let audioThumbnailUrl = url;
    if (isAudioFile && url) {
        if (url.includes('_Audio.wav')) {
            audioThumbnailUrl = url.replace('_Audio.wav', '_Graph.png');
        } else if (url.includes('_Audio.WAV')) {
            audioThumbnailUrl = url.replace('_Audio.WAV', '_Graph.png');
        } else if (url.includes('elephant.wav')) {
            audioThumbnailUrl = url.replace('Audio Files', 'image').replace('elephant.wav', 'elephant.png');
        } else {
            audioThumbnailUrl = url.replace(/\.wav$/i, '_Graph.png');
        }
    }

    const intactness = getIntactnessRangeCategory(convertToNumber(score ?? 0));

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '300px',
                width: '100%',
                backgroundColor: '#151E15',
                borderRadius: '10px',
                overflow: 'hidden',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                position: 'relative',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.35)',
                }
            }}
        >
            {/* Top Gradient & Info Overlay */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 3,
                    p: '8px 12px',
                    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.3) 70%, transparent 100%)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                }}
            >
                <Box sx={{ maxWidth: 'calc(100% - 38px)' }}>
                    <Typography
                        variant="body1"
                        sx={{
                            fontWeight: 600,
                            fontSize: '15px',
                            color: '#FFFFFF',
                            lineHeight: 1.2,
                            textShadow: '0 1px 3px rgba(0,0,0,0.9)',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {title}
                    </Typography>
                    <Chip
                        size="small"
                        icon={
                            isAudioFile ? (
                                <HearingIcon sx={{ fontSize: '12px !important', color: '#86efac !important' }} />
                            ) : (
                                <CameraAltIcon sx={{ fontSize: '12px !important', color: '#86efac !important' }} />
                            )
                        }
                        label={isAudioFile ? 'BioAcoustic' : 'Camera Trap'}
                        sx={{
                            mt: 0.3,
                            height: '18px',
                            fontSize: '9.5px',
                            fontWeight: 600,
                            backgroundColor: 'rgba(0, 0, 0, 0.65)',
                            color: '#86efac',
                            border: '1px solid rgba(134, 239, 172, 0.25)',
                            backdropFilter: 'blur(4px)',
                            '& .MuiChip-label': { px: 0.6 }
                        }}
                    />
                </Box>

                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isSelected}
                            onChange={onToggleSelect}
                            size="small"
                            sx={{
                                color: '#FFFFFF',
                                p: 0.3,
                                '&.Mui-checked': { color: '#10B981' }
                            }}
                        />
                    }
                    label=""
                    sx={{ m: 0 }}
                />
            </Box>

            {/* Media Area */}
            <Box sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {isAudioFile ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        {/* Audio Cover / Spectrogram */}
                        <Box
                            sx={{
                                height: '195px',
                                width: '100%',
                                backgroundColor: '#09150e',
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                            }}
                        >
                            {!imgError && audioThumbnailUrl && !audioThumbnailUrl.endsWith('.wav') ? (
                                <img
                                    src={audioThumbnailUrl}
                                    alt="Audio Spectrogram"
                                    onError={() => setImgError(true)}
                                    style={{
                                        height: '100%',
                                        width: '100%',
                                        objectFit: 'cover',
                                        objectPosition: 'center',
                                        display: 'block'
                                    }}
                                />
                            ) : (
                                /* Presentable BioAcoustic Soundwave Visualizer Fallback */
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'radial-gradient(circle at center, #133324 0%, #08160f 80%)',
                                        p: 1.5,
                                        position: 'relative'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '3px',
                                            height: '45px',
                                            mb: 0.8
                                        }}
                                    >
                                        {[20, 38, 15, 44, 26, 48, 32, 16, 40, 28, 12, 36, 46, 22, 34, 14].map((h, i) => (
                                            <Box
                                                key={i}
                                                sx={{
                                                    width: '3.5px',
                                                    height: `${h}px`,
                                                    borderRadius: '2px',
                                                    backgroundColor: i % 2 === 0 ? '#10B981' : '#34D399',
                                                    opacity: 0.85
                                                }}
                                            />
                                        ))}
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                        <GraphicEqIcon sx={{ color: '#86efac', fontSize: '15px' }} />
                                        <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#A7F3D0', letterSpacing: '0.4px' }}>
                                            BioAcoustic Recording
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Audio Player Bar */}
                        <Box
                            sx={{
                                px: 1,
                                py: 0.6,
                                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderTop: '1px solid rgba(255,255,255,0.05)'
                            }}
                        >
                            <audio
                                id={`audio-${index}`}
                                key={url}
                                controls
                                style={{
                                    width: '100%',
                                    height: '32px',
                                    borderRadius: '6px',
                                    outline: 'none'
                                }}
                            >
                                <source src={url} type="audio/wav" />
                                Your browser does not support the audio element.
                            </audio>
                        </Box>
                    </Box>
                ) : (
                    /* Camera Trap Image */
                    <Box
                        sx={{
                            height: '100%',
                            width: '100%',
                            backgroundColor: '#0a120a',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        {!imgError && url ? (
                            <img
                                src={url}
                                alt={typeof title === 'string' ? title : 'Species'}
                                onError={() => setImgError(true)}
                                style={{
                                    height: '100%',
                                    width: '100%',
                                    objectFit: 'cover',
                                    objectPosition: 'center',
                                    display: 'block'
                                }}
                            />
                        ) : (
                            /* Clean Camera Placeholder Fallback */
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'radial-gradient(circle at center, #15261b 0%, #0a140d 80%)',
                                    p: 2,
                                    textAlign: 'center'
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '46px',
                                        height: '46px',
                                        borderRadius: '50%',
                                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                        border: '1px solid rgba(16, 185, 129, 0.25)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mb: 0.8
                                    }}
                                >
                                    <CameraAltIcon sx={{ color: '#86efac', fontSize: '24px' }} />
                                </Box>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#D1FAE5' }}>
                                    {title}
                                </Typography>
                                <Typography sx={{ fontSize: '10.5px', color: '#9CA3AF', mt: 0.3 }}>
                                    Sensor Capture
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Bottom Metadata Bar */}
            <Box
                sx={{
                    px: '14px',
                    py: '8px',
                    backgroundColor: '#111911',
                    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 'auto',
                    minHeight: '46px'
                }}
            >
                <Box>
                    <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#9CA3AF', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                        {dateTitle || 'Date'}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#F3F4F6', lineHeight: 1.2 }}>
                        {date || 'N/A'}
                    </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#9CA3AF', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                        {scoreTitle || 'Score'}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 800, color: intactness.color, lineHeight: 1.2 }}>
                        {score || 'N/A'}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

const DownloadCardComponent: React.FC<DownloadCardComponentProps> = ({
    title,
    btnName,
    handleButtonEvent,
    data,
    dataId,
    checkboxes,
    dateTitle,
    date,
    scoreTitle,
    score,
    url
}) => {
    const [enabled, setEnabled] = useState(false);
    const [selectedData, setSelectedData] = useState<any[]>([]);

    const handleCheckboxChange = (index: number) => {
        const updatedSelectedData = [...selectedData];
        const selectedItem = {
            type: data[index],
            id: dataId[index],
            date: date[index],
            score: score[index],
            url: url[index],
        };

        const itemIndex = updatedSelectedData.findIndex((item) => item.id === selectedItem.id);
        if (itemIndex >= 0) {
            updatedSelectedData.splice(itemIndex, 1);
        } else {
            updatedSelectedData.push(selectedItem);
        }

        setSelectedData(updatedSelectedData);
        setEnabled(updatedSelectedData.length > 0);
    };

    const handleClick = () => {
        handleButtonEvent(selectedData);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', m: 2 }}>
                <Box>
                    <Typography sx={{ fontSize: '24px', fontWeight: 600, color: '#151E15' }}>
                        {title}
                    </Typography>
                </Box>

                <Box>
                    <Button
                        onClick={handleClick}
                        disabled={!enabled}
                        sx={{
                            backgroundColor: enabled ? '#005F54' : '#c4c4c4',
                            color: '#fff !important',
                            padding: '10px 20px',
                            fontWeight: 600,
                            borderRadius: '8px',
                            textTransform: 'none',
                        }}
                    >
                        {btnName}
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ marginTop: '20px' }}>
                {data.map((item, index) => {
                    const isSelected = selectedData.some((s) => s.id === dataId[index]);
                    return (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={url?.[index] ? `${url[index]}-${index}` : (dataId?.[index] ? `${dataId[index]}-${index}` : index)}>
                            <SpeciesCardItem
                                index={index}
                                title={data[index]}
                                dateTitle={dateTitle ? dateTitle[index] : 'Date'}
                                date={date[index]}
                                scoreTitle={scoreTitle ? scoreTitle[index] : 'Score'}
                                score={score[index]}
                                url={url[index]}
                                isSelected={isSelected}
                                onToggleSelect={() => handleCheckboxChange(index)}
                            />
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
};

export default DownloadCardComponent;